import { useState, useEffect, useRef } from "react";
import { sortHigh, sortLow, RiskLevel } from "@intelligentlilli/lilli-utils";
import { getSearchResults, getDaysSinceRiskStatusChanged } from "../Utils";
import { useAppSelector } from "State/hooks";
import { ServiceUserDataState } from "State/slices/serviceUsersData-slice";
import { INCOMPLETE_STATUSES } from "../Constants/filters";

export const useGetServiceUserList = (
  selectedFilter: string,
  searchValue: string
) => {
  const hasSearchTerm = searchValue?.trim()?.length > 0;
  const searchValueRef = useRef<string | undefined>();
  // Redux
  const serviceUsers = useAppSelector((state) => state.serviceUsers);
  const serviceUsersData = useAppSelector((state) => state.serviceUsersData);
  const staff = useAppSelector((state) => state.staff);
  // Local state
  const [serviceUsersToDisplay, setServiceUsersToDisplay] = useState([]);
  const [sortAZ, setSortAZ] = useState<boolean | undefined>();
  const [sortRisk, setSortRisk] = useState<boolean | undefined>();
  const [sortInstall, setSortInstall] = useState<boolean | undefined>();

  // When the selected filter, search term or sorting preference changes update the residents to display
  useEffect(() => {
    // Function to get the unfiltered list of residents augmented with risk and installation status
    const getBaseSUs = (
      serviceUsers: any[],
      serviceUsersData: ServiceUserDataState
    ) => {
      // Make a deep copy of the residents so that they can be modified
      const deepSUsCopy = JSON.parse(JSON.stringify(serviceUsers));
      // Add in the risk status of each resident
      return deepSUsCopy.map((serviceUser) => {
        const installationStatus =
          serviceUser?.serviceUserProperties?.installationStatus;
        const installFailReason = serviceUser?.serviceUserProperties?.installationFailureReason;
        const riskStatus = serviceUsersData?.[serviceUser.id]?.riskStatus;
        const daysSinceRiskStatusChanged = getDaysSinceRiskStatusChanged(
          serviceUsersData?.[serviceUser.id]?.riskScores,
          serviceUser?.firstEventTimestamp
        );
        return {
          ...serviceUser,
          // For residents that are post install but don't have a risk status (recent installs) add them to the expected SUs.
          riskStatus: riskStatus ?? (installationStatus === "complete" && 0),
          installationStatus: installationStatus || "incomplete",
          installFailReason,
          daysSinceRiskStatusChanged: daysSinceRiskStatusChanged,
        };
      });
    };
    let filteredServiceUsers = getBaseSUs(serviceUsers, serviceUsersData) || [];
    // Filter predicates for named filters
    const hasFriendOrFamily = (su: any) =>
      su?.viewers?.some((viewer: any) => {
        const staffMember = staff?.find(
          (staffItem: any) => staffItem.id === viewer.id
        );
        return staffMember?.roles[0] === "friend-or-family";
      });

    const filterKeys: Record<string, (su: any) => boolean> = {
      friends_and_family: (su) => hasFriendOrFamily(su),
      no_friends_and_family: (su) => !hasFriendOrFamily(su),
      incomplete: (su) =>
        !su?.installationStatus ||
        INCOMPLETE_STATUSES.includes(su?.installationStatus),
      complete: (su) => su?.installationStatus === "complete",
    };

    // Apply the filter
    if (selectedFilter !== "all") {
      filteredServiceUsers = filteredServiceUsers.filter((su) => {
        // Check named filter predicates
        const predicate = filterKeys[selectedFilter];
        if (predicate) return predicate(su);

        // Check risk level filters (AT_RISK, UNEXPECTED, EXPECTED)
        if (RiskLevel[su.riskStatus] === selectedFilter) return true;

        // Fallback: match installation status directly
        return su.installationStatus === selectedFilter;
      });
    }

    // Apply the search
    if (hasSearchTerm) {
      filteredServiceUsers = getSearchResults(
        searchValue,
        filteredServiceUsers
      );
    }

    // When the user is searching, reset the sorting so that the most relevant results always appear at the top
    if (searchValue !== searchValueRef.current) {
      setSortAZ(undefined);
      setSortInstall(undefined);
      setSortRisk(undefined);
    } else {
      // Apply sorting
      if (sortAZ === true) {
        filteredServiceUsers = sortHigh(filteredServiceUsers, "userstring");
      }
      if (sortAZ === false) {
        filteredServiceUsers = sortLow(filteredServiceUsers, "userstring");
      }
      if (sortRisk === true) {
        filteredServiceUsers = sortLow(filteredServiceUsers, "riskStatus");
      }
      if (sortRisk === false) {
        filteredServiceUsers = sortHigh(filteredServiceUsers, "riskStatus");
      }
      if (sortInstall === true) {
        filteredServiceUsers = sortLow(
          filteredServiceUsers,
          "installationStatus"
        );
      }
      if (sortInstall === false) {
        filteredServiceUsers = sortHigh(
          filteredServiceUsers,
          "installationStatus"
        );
      }
    }

    // Update the ref for the current search term
    searchValueRef.current = searchValue;
    setServiceUsersToDisplay(filteredServiceUsers);
  }, [
    serviceUsers,
    serviceUsersData,
    selectedFilter,
    hasSearchTerm,
    searchValue,
    sortInstall,
    setSortRisk,
    setSortInstall,
    setSortAZ,
    sortAZ,
    sortRisk,
    staff,
  ]);

  return {
    serviceUsersToDisplay,
    sortRisk,
    sortAZ,
    sortInstall,
    setSortRisk,
    setSortInstall,
    setSortAZ,
  };
};
